import Vue from 'vue';
import store from '../store/store.js';
import VueRouter from 'vue-router';
import { Role } from './_helper';
import routes from '../routes';

function lazyLoad(view) {
  return () =>
    import(/* webpackChunkName: "[request]" */ `@/views/${view}.vue`);
}
function lazyLoadComponent(view) {
  return () =>
    import(/* webpackChunkName: "[request]" */ `@/components/${view}.vue`);
}

Vue.use(VueRouter);

const all_routes = routes.concat([
  {
    path: '/',
    name: 'Home',
    component: lazyLoad('Home'),
    meta: {
      authorize: [
        Role.ViewFinance,
        Role.ViewCustomer,
        Role.ViewAcademic,
        Role.ViewStaff,
        Role.CloseAccount,
        Role.Super,
      ],
      requiresAuth: true,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: lazyLoadComponent('auth/Login'),
    meta: { authorize: [], requiresAuth: false },
  },
  {
    path: '/pwd-reset',
    name: 'ResetPassword',
    component: lazyLoadComponent('auth/ResetPassword'),
    meta: { authorize: [] },
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: lazyLoadComponent('error/Unauthorized'),
    meta: { authorize: [], requiresAuth: false },
  },
  {
    path: '/invoices',
    name: 'Invoices',
    component: lazyLoad('Invoices'),
    meta: { authorize: [Role.ViewFinance], requiresAuth: true },
  },
  {
    path: '/payments',
    name: 'Payments',
    component: lazyLoad('Payments'),
    meta: { authorize: [Role.ViewFinance], requiresAuth: true },
  },
  {
    path: '/invoices/generate-bill',
    name: 'GenerateBill',
    component: lazyLoadComponent('billing/GenerateBills'),
    meta: { authorize: [Role.ViewFinance], requiresAuth: true },
  },
  {
    path: '/billing/client-statement',
    name: 'ClientStatement',
    component: lazyLoad('ClientStatement'),
    meta: { authorize: [Role.ViewFinance], requiresAuth: true },
  },
  {
    path: '/enrollments',
    name: 'Enrollment',
    component: lazyLoad('Enrollment'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/attendance',
    name: 'Attendance',
    component: lazyLoad('AttendanceSheet'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/monthly-attendance',
    name: 'MonthlyAttendance',
    component: lazyLoad('MonthlyAttendance'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/attendance/executive-summary',
    name: 'AttendanceExecutiveSummary',
    component: lazyLoad('AttendanceExecutiveSummary'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/timesheet',
    name: 'Timesheet',
    component: lazyLoad('Timesheet'),
    meta: { authorize: [Role.ViewStaff], requiresAuth: true },
  },
  {
    path: '/payroll-scheduling',
    name: 'PayrollAndScheduling',
    component: lazyLoadComponent('payroll/PayrollAndScheduling'),
    meta: { authorize: [Role.ViewStaff], requiresAuth: true },
  },
  {
    path: '/payroll/report/:data',
    name: 'PayrollReport',
    component: lazyLoadComponent('payroll/SummaryReportNew'),
    meta: { authorize: [Role.ViewStaff], requiresAuth: true },
  },
  {
    path: '/edit-profile',
    name: 'EditProfile',
    component: lazyLoadComponent('user/EditProfile'),
    meta: { authorize: [], requiresAuth: true },
  },
  {
    path: '/checklist/daily',
    name: 'ChecklistDaily',
    component: lazyLoad('DailyChecklist'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/checklist/summary',
    name: 'ChecklistDailySummary',
    component: lazyLoad('DailyChecklistSummary'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/checklist/student',
    name: 'ChecklistStudentSummary',
    component: lazyLoad('StudentChecklistSummary'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/checklist/executive',
    name: 'ExecutiveChecklistSummary',
    component: lazyLoad('ExecutiveChecklistSummary'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/print-attendance/:data',
    name: 'PrintAttendance',
    component: lazyLoadComponent('billing/PrintAttendance'),
    meta: { authorize: [Role.ViewCustomer], requiresAuth: true },
  },
  {
    path: '/google-meet/session-audit',
    name: 'MeetSessionAudit',
    component: lazyLoad('GoogleMeetSessionAudit'),
    meta: {
      authorize: [Role.ViewCustomer, Role.ViewStaff],
      requiresAuth: true,
    },
  },
  {
    path: '/google-meet/tutor-performance',
    name: 'MeetTutorPerformance',
    component: lazyLoad('GoogleMeetTutorPerformance'),
    meta: {
      authorize: [Role.ViewCustomer, Role.ViewStaff],
      requiresAuth: true,
    },
  },
]);

const router = new VueRouter({
  routes: all_routes,
});

function isAuthorized(authorize) {
  if (!authorize || authorize.length < 1) {
    return true;
  }
  let hasModule = false;
  const userModules = store.getters['auth/userModules'];
  if (userModules && userModules.length > 0) {
    hasModule = userModules.some((m) => m && m.path === 'reports');
  }

  const userRoles = store.getters['auth/userRoles'];
  if (hasModule && userRoles && userRoles.length > 0) {
    return userRoles.some((r) => authorize.includes(r));
  } else {
    store.dispatch('messages/addErrorMessage', 'Unauthorized Access');
    return false;
  }
}

router.beforeEach((to, from, next) => {
  if (
    to.matched.some(
      (record) =>
        record.path.startsWith('/secure/') || record.meta.requiresAuth,
    )
  ) {
    const { authorize } = to.meta;
    if (store.getters['auth/isLoggedIn']) {
      //is logged in and has authorized permission
      if (isAuthorized(authorize)) {
        next();
        return;
      } else {
        //Is logged in, but not authorized
        // store.dispatch("processLogout");
        next('/unauthorized');
      }
    }
    //not logged in
    else {
      //Check if jwt token exists and attempt to automatically log in
      store
        .dispatch('auth/remoteJwtTokenCheck')
        .then(() => {
          //check if user is authorized
          if (isAuthorized(authorize)) {
            next();
          } else {
            //not authorized
            store.dispatch('auth/processLogout');
            next('/login');
          }
        })
        .catch(() => {
          // console.log('fail jwt token check')
          store.dispatch('auth/processLogout');
          next('/login');
        });
    }
  } else {
    //accessing non-secured page
    next();
  }
});

export default router;
